import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  restore,
  sample,
} from 'effector'
import { persist } from 'effector-storage/local'
import { interval } from 'patronum'
import { model as i18n, t } from '~/core/i18n'
import { register } from '~/core/repository'
import { hooks } from '~/core/router'
import { profilesModel } from '~/entities/profiles'
import { model as session } from '~/entities/session'
import { noop } from '~/shared/effector'
import { aye } from '~/shared/helpers'
import { $subscriberPin, unlockId } from '../content'
import { setUnlocked as setUnlockedProfile } from '../profiles'

// Constants for pin lockout feature
const MAX_FAILED_ATTEMPTS = 5
const LOCKOUT_DURATION_SECONDS = 60
const TIMER_INTERVAL = 1000
export interface IPinLockPopupProps {
  condition: boolean
  disabled: boolean
  errorMessage: string
  id: number | null
  invalid: boolean
  locked: boolean
  name: string
  onChange: (value: string) => void
  onComplete: (value: string) => void
  valueAfterInvalid: string // it is necessary not to lose the character input after the arrival of an external invalid prop. which leads to the component being unmounted
  profileUnlock?: boolean
}

export const emptyPinLockState: IPinLockPopupProps = {
  condition: false,
  disabled: false,
  errorMessage: '',
  id: null,
  invalid: false,
  locked: false,
  name: '',
  onChange: () => null,
  onComplete: () => null,
  valueAfterInvalid: '',
  profileUnlock: false,
}

// Pin lockout state interface
interface IPinLockoutState {
  failedAttempts: number
  lockoutEndTime: number | null // timestamp when lockout ends
}

const initialLockoutState: IPinLockoutState = {
  failedAttempts: 0,
  lockoutEndTime: null,
}

// Events for lockout management
const incrementFailedAttempts = createEvent()
const resetFailedAttempts = createEvent()
const startLockout = createEvent()
const endLockout = createEvent()

// Interval for countdown timer
const { tick: lockoutTick } = interval({
  timeout: TIMER_INTERVAL,
  start: startLockout,
  stop: endLockout,
})

// Stores for pin lockout feature
export const $pinLockoutState = createStore<IPinLockoutState>(
  initialLockoutState
)
  .on(incrementFailedAttempts, (state) => {
    const newFailedAttempts = state.failedAttempts + 1
    if (newFailedAttempts >= MAX_FAILED_ATTEMPTS) {
      return {
        failedAttempts: 0,
        lockoutEndTime: Date.now() + LOCKOUT_DURATION_SECONDS * 1000,
      }
    }
    return {
      ...state,
      failedAttempts: newFailedAttempts,
    }
  })
  .on(resetFailedAttempts, () => initialLockoutState)
  .on(endLockout, () => initialLockoutState)

// Persist lockout state to localStorage
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $pinLockoutState,
  key: 'tria__pin-lock_lockout',
  fail: noop,
})

// Calculate remaining seconds from lockout state
const calculateRemainingSecondsFx = attach({
  source: $pinLockoutState,
  mapParams: (_, { lockoutEndTime }) => lockoutEndTime,
  effect: createEffect((lockoutEndTime: number | null) => {
    const now = Date.now()
    if (lockoutEndTime && lockoutEndTime > now) {
      return Math.ceil((lockoutEndTime - now) / 1000)
    }
    return 0
  }),
})

// Countdown timer store
export const $lockoutRemainingSeconds = createStore<number>(0).on(
  calculateRemainingSecondsFx.doneData,
  (_, seconds) => seconds
)

export const $disabled = $lockoutRemainingSeconds.map((seconds) => seconds > 0)

// Recalculate remaining seconds on each tick (more accurate than decrementing)
sample({
  clock: lockoutTick,
  target: calculateRemainingSecondsFx,
})

// Check if currently locked out
export const $isLockedOut = $pinLockoutState.map(
  (state) => state.lockoutEndTime !== null && state.lockoutEndTime > Date.now()
)

// Lockout error message with countdown
export const $lockoutErrorMessage = combine(
  $lockoutRemainingSeconds,
  $isLockedOut,
  (remainingSeconds, isLockedOut) => {
    if (isLockedOut && remainingSeconds > 0) {
      return t('enter_pin_wait_message', { count: remainingSeconds })
    }
    return ''
  }
)

// Start lockout timer when lockout begins
sample({
  clock: $pinLockoutState,
  filter: (state) =>
    state.lockoutEndTime !== null && state.lockoutEndTime > Date.now(),
  target: calculateRemainingSecondsFx,
})

sample({
  clock: calculateRemainingSecondsFx.doneData,
  filter: (seconds) => seconds > 0,
  target: startLockout,
})

// End lockout when countdown reaches 0
sample({
  clock: $lockoutRemainingSeconds,
  source: $pinLockoutState,
  filter: (state, seconds) => seconds <= 0 && state.lockoutEndTime !== null,
  target: endLockout,
})

export const openPinLockPopup = createEvent<IPinLockPopupProps>()

const $openedPinLockPopup = restore(openPinLockPopup, null).reset(unlockId)
// Check lockout state on popup open (in case page was reloaded during lockout)
sample({
  clock: openPinLockPopup,
  target: calculateRemainingSecondsFx,
})

export const closePinLockPopup = createEvent()
export const autoSubmitPinCode = createEvent<{ value: string }>()
export const changePinCode = createEvent<{ value: string }>()
export const resetValidPinCode = createEvent()

export const $isValidPinCode = createStore<boolean>(false) //
  .reset([openPinLockPopup, resetValidPinCode, closePinLockPopup])

export const $pinLockPopup = createStore<IPinLockPopupProps>(emptyPinLockState)
  .on(openPinLockPopup, (_, payload) => ({ ...payload, condition: true }))
  .reset(closePinLockPopup)

// Update popup error message during countdown (also handles initial lockout state on popup open)
sample({
  clock: $lockoutRemainingSeconds,
  source: $pinLockPopup,
  filter: (popup, seconds) => popup.condition && seconds > 0,
  fn: (popup, seconds) => ({
    ...popup,
    invalid: true,
    disabled: true,
    errorMessage: t('enter_pin_wait_message', { count: seconds }),
  }),
  target: $pinLockPopup,
})

// Clear error message when lockout ends
sample({
  clock: endLockout,
  source: $pinLockPopup,
  filter: (popup) => popup.condition,
  fn: (popup) => ({
    ...popup,
    invalid: false,
    disabled: false,
    errorMessage: '',
  }),
  target: $pinLockPopup,
})

export const $pinLockPopupDescription = i18n.translated`locked_popup_hint`
export const $enterPinDescription = i18n.translated`enter_pin`
const $pinLockPopupErrorMessage = i18n.translated`pin_locked_error`

sample({
  clock: $isValidPinCode,
  source: $openedPinLockPopup,
  filter: (targetContent, valid) =>
    aye(valid) && targetContent != null && !targetContent.profileUnlock,
  fn: (targetContent) => targetContent!.id,
  target: unlockId,
})

sample({
  clock: $isValidPinCode,
  source: $openedPinLockPopup,
  filter: (targetContent, valid) =>
    aye(valid) && targetContent != null && Boolean(targetContent.profileUnlock),
  fn: (targetContent) => targetContent!.id,
  target: setUnlockedProfile,
})

sample({
  clock: $isValidPinCode,
  source: $openedPinLockPopup,
  filter: (targetContent, valid) => aye(valid) && targetContent != null,
  // Unlock samples above run first (same clock, earlier declaration) so
  // callers can replay queued actions that reset pending on closePinLockPopup.
  target: [resetFailedAttempts, closePinLockPopup],
})

// Block submission and show lockout message when locked out
sample({
  clock: autoSubmitPinCode,
  source: {
    popup: $pinLockPopup,
    isLockedOut: $isLockedOut,
    lockoutMessage: $lockoutErrorMessage,
  },
  filter: ({ isLockedOut }) => isLockedOut,
  fn: ({ popup, lockoutMessage }) => ({
    ...popup,
    invalid: true,
    valueAfterInvalid: '',
    errorMessage: lockoutMessage,
  }),
  target: $pinLockPopup,
})

// Handle pin submission when not locked out
sample({
  clock: autoSubmitPinCode,
  source: {
    pin: $subscriberPin,
    popup: $pinLockPopup,
    errorMessageLocale: $pinLockPopupErrorMessage,
    isLockedOut: $isLockedOut,
  },
  filter: ({ isLockedOut }) => !isLockedOut,
  fn: ({ pin, popup, errorMessageLocale }, { value }) => {
    const invalid = pin !== value
    const errorMessage = invalid ? errorMessageLocale : ''
    return {
      ...popup,
      invalid,
      valueAfterInvalid: '',
      errorMessage,
    }
  },
  target: $pinLockPopup,
})

sample({
  clock: autoSubmitPinCode,
  source: { pin: $subscriberPin, isLockedOut: $isLockedOut },
  filter: ({ isLockedOut }) => !isLockedOut,
  fn: ({ pin }, { value }) => pin === value,
  target: $isValidPinCode,
})

// Increment failed attempts on wrong pin (when not locked out)
sample({
  clock: autoSubmitPinCode,
  source: { pin: $subscriberPin, isLockedOut: $isLockedOut },
  filter: ({ pin, isLockedOut }, { value }) => !isLockedOut && pin !== value,
  target: incrementFailedAttempts,
})

// remove error state when new valid value provided (only if not locked out)
sample({
  clock: changePinCode,
  source: { popup: $pinLockPopup, isLockedOut: $isLockedOut },
  filter: ({ popup, isLockedOut }, { value }) =>
    popup.invalid && Boolean(value.length) && !isLockedOut,
  fn: ({ popup }, { value }) => {
    return {
      ...popup,
      invalid: false,
      valueAfterInvalid: value,
      errorMessage: '',
    }
  },
  target: $pinLockPopup,
})

//
// Reset and hide pin lock popup on any route leave
//

const navigationLeftHook = createEvent()

// register the navigation hook
hooks.register(hooks.Left, navigationLeftHook)

sample({
  clock: navigationLeftHook,
  fn: () => undefined,
  target: closePinLockPopup,
})

export const unlockContent =
  createEvent<Pick<IPinLockPopupProps, 'id' | 'name' | 'locked'>>()

sample({
  clock: unlockContent,
  fn: ({ id, name, locked }) => ({
    ...emptyPinLockState,
    id,
    name,
    locked,
  }),
  target: openPinLockPopup,
})

//
// Reset and hide pin lock popup on any session change
//

sample({
  clock: [session.$accountNumber, profilesModel.$activeProfileId],
  fn: () => undefined,
  target: closePinLockPopup,
})

/**
 * Disabling automatic scrolling when the pinlock popup is open
 */
export const $isBannerAutoScrollEnabled = combine(
  $pinLockPopup,
  (pinLockPopup) => (autoScrollEnabled: boolean) =>
    autoScrollEnabled && !pinLockPopup.condition
)

const $popupName = $pinLockPopup.map(
  (pinLockPopup) => pinLockPopup.name || t('enter_pin')
)
const $popupDescription = $pinLockPopup.map((pinLockPopup) =>
  pinLockPopup.name ? t('locked_popup_hint') : ''
)

//
//
//

register($pinLockPopup, '$features/pin-lock/popup')
register($popupName, '$features/pin-lock/popupName')
register($popupDescription, '$features/pin-lock/popupDescription')
// register(openPinLockPopup, 'features/pin-lock/openPopup')
register(closePinLockPopup, 'features/pin-lock/closePopup')
register(changePinCode, 'features/pin-lock/changePinCode')
register(autoSubmitPinCode, 'features/pin-lock/autoSubmitPinCode')
register(unlockContent, 'features/pin-lock/unlockContent')
register(
  $isBannerAutoScrollEnabled,
  '|features/pin-lock/isBannerAutoScrollEnabled'
)
register($isLockedOut, '$features/pin-lock/isLockedOut')
register($lockoutRemainingSeconds, '$features/pin-lock/lockoutRemainingSeconds')
register($lockoutErrorMessage, '$features/pin-lock/lockoutErrorMessage')
